import { z } from 'zod';
// ─── Task ──────────────────────────────────────────────────────────────
export const PriorityGroupSchema = z.enum(['today', 'high', 'medium', 'low', 'backlog']);
export const ChunkSchema = z.object({
    id: z.string(),
    title: z.string(),
    est: z.string(),
    done: z.boolean().default(false),
    active: z.boolean().optional(),
});
export const TaskSchema = z.object({
    id: z.string(),
    kind: z.enum(['task', 'event', 'habit', 'note']).default('task'),
    title: z.string().min(1),
    est: z.string().default('30m'),
    tag: z.string().default('Inbox'),
    due: z.string().default(''),
    impact: z.number().int().min(1).max(10).nullable().default(null),
    urgency: z.number().int().min(1).max(10).nullable().default(null),
    urgDays: z.number().int().nullable().default(null),
    urgFactor: z.number().default(0.6),
    group: PriorityGroupSchema.default('backlog'),
    order: z.number().int().default(0),
    done: z.boolean().default(false),
    active: z.boolean().default(false),
    score: z.number().int().min(0).max(100).nullable().optional(),
    chunks: z.array(ChunkSchema).optional(),
    source: z.string().optional(),
    createdAt: z.number().default(() => Date.now()),
    updatedAt: z.number().default(() => Date.now()),
});
export const TaskCreateSchema = TaskSchema.partial({
    id: true, kind: true, est: true, tag: true, due: true, impact: true, urgency: true,
    urgDays: true, urgFactor: true, group: true, order: true, done: true, active: true,
    createdAt: true, updatedAt: true,
}).required({ title: true });
export const TaskUpdateSchema = TaskSchema.partial().omit({ id: true });
// ─── Block (calendar schedule slot) ────────────────────────────────────
export const BlockCategorySchema = z.enum([
    'blockDeep', 'blockAdmin', 'blockMeeting', 'blockLunch', 'blockPersonal', 'blockBuffer',
    'blockCreative', 'blockLearning', 'blockSocial', 'blockFitness', 'blockTravel', 'blockRoutine',
]);
// Inline-Todos innerhalb eines Workblocks. Drop eines Tasks auf einen Block fügt hier einen Eintrag an.
// Wird separat vom `taskId` (Single-Task-Link) verwaltet.
export const BlockContentItemSchema = z.object({
    taskId: z.string(),
    done: z.boolean().default(false),
});
export const BlockSchema = z.object({
    id: z.string(),
    dayIdx: z.number().int().min(0).max(6),
    s: z.number(),
    e: z.number(),
    t: z.string(),
    cat: BlockCategorySchema.default('blockDeep'),
    anchor: z.boolean().default(false),
    done: z.boolean().default(false),
    taskId: z.string().nullable().default(null),
    chunkId: z.string().nullable().default(null),
    source: z.enum(['user', 'gcal']).default('user'),
    gcalEventId: z.string().nullable().default(null),
    contents: z.array(BlockContentItemSchema).nullable().default(null),
    description: z.string().nullable().default(null),
    location: z.string().nullable().default(null),
    meetingUrl: z.string().nullable().default(null),
    weekStart: z.number(),
    createdAt: z.number().default(() => Date.now()),
});
export const BlockCreateSchema = BlockSchema.partial({
    id: true, anchor: true, done: true, taskId: true, chunkId: true,
    source: true, gcalEventId: true, createdAt: true,
});
export const BlockUpdateSchema = BlockSchema.partial().omit({ id: true });
// ─── Activity Event (from mac helper) ─────────────────────────────────
export const ActivityEventSchema = z.object({
    ts: z.number(),
    app: z.string(),
    title: z.string().optional().nullable(),
    url: z.string().optional().nullable(),
    bundleId: z.string().optional().nullable(),
    idleSec: z.number().int().nonnegative().optional().nullable(),
    category: z.string().optional().nullable(),
    taskId: z.string().nullable().optional(),
});
export const ActivityIngestSchema = z.object({
    events: z.array(ActivityEventSchema).min(1),
});
// 15-min aggregated window for a day
export const ActivityWindowSchema = z.object({
    bucket: z.number(), // start-time in hours (e.g. 10.25)
    durationMin: z.number(), // actual active minutes in bucket (1-15)
    dominantApp: z.string().nullable(),
    dominantTitle: z.string().nullable(),
    category: z.string().nullable(),
    breakdown: z.array(z.object({
        app: z.string(),
        title: z.string().nullable(),
        sec: z.number(),
    })),
    eventCount: z.number(),
});
// Aggregated summary across an arbitrary [from, to] range.
export const ActivitySummarySchema = z.object({
    from: z.number(),
    to: z.number(),
    totalSec: z.number(),
    byCategory: z.record(z.string(), z.number()),
    byApp: z.array(z.object({
        app: z.string(),
        sec: z.number(),
        titles: z.array(z.object({ title: z.string().nullable(), sec: z.number() })),
    })),
    byTask: z.array(z.object({ taskId: z.string(), sec: z.number() })),
    byDay: z.array(z.object({
        dayMs: z.number(),
        totalSec: z.number(),
        byCategory: z.record(z.string(), z.number()),
    })),
});
// ─── Settings ─────────────────────────────────────────────────────────
export const InterventionTimersSchema = z.object({
    stage1: z.number().default(120000),
    stage2: z.number().default(300000),
    stage3: z.number().default(720000),
});
export const SettingsSchema = z.object({
    darkMode: z.boolean().default(false),
    userName: z.string().default('Tom'),
    workStart: z.number().default(8),
    workEnd: z.number().default(19),
    interventionTimers: InterventionTimersSchema.default({
        stage1: 120000, stage2: 300000, stage3: 720000,
    }),
    blockSites: z.array(z.string()).default([]),
    gcalEnabled: z.boolean().default(false),
    gcalCalendarIds: z.array(z.string()).default([]),
    sidebarWidth: z.number().default(340),
    sidebarCollapsed: z.boolean().default(false),
});
// ─── Google Calendar ──────────────────────────────────────────────────
export const GoogleCalendarSchema = z.object({
    id: z.string(),
    summary: z.string(),
    backgroundColor: z.string().optional(),
    foregroundColor: z.string().optional(),
    primary: z.boolean().optional(),
    selected: z.boolean().default(true),
});
export const GoogleEventSchema = z.object({
    id: z.string(),
    calendarId: z.string(),
    summary: z.string().default(''),
    description: z.string().optional(),
    start: z.object({
        dateTime: z.string().optional(),
        date: z.string().optional(),
        timeZone: z.string().optional(),
    }),
    end: z.object({
        dateTime: z.string().optional(),
        date: z.string().optional(),
        timeZone: z.string().optional(),
    }),
    status: z.string().optional(),
    htmlLink: z.string().optional(),
    updated: z.string().optional(),
});
// ─── Inbox (claude-code POSTs) ────────────────────────────────────────
export const InboxItemSchema = z.object({
    id: z.string(),
    ts: z.number(),
    title: z.string(),
    est: z.string().optional(),
    tag: z.string().optional(),
    due: z.string().optional(),
    impact: z.number().optional(),
    source: z.string().default('claude-code'),
});
export const InboxPostSchema = z.union([
    TaskCreateSchema,
    z.object({ tasks: z.array(TaskCreateSchema) }),
]);
// ─── SSE Event envelope ───────────────────────────────────────────────
export const StreamEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('task.created'), data: TaskSchema }),
    z.object({ type: z.literal('task.updated'), data: TaskSchema }),
    z.object({ type: z.literal('task.deleted'), data: z.object({ id: z.string() }) }),
    z.object({ type: z.literal('block.created'), data: BlockSchema }),
    z.object({ type: z.literal('block.updated'), data: BlockSchema }),
    z.object({ type: z.literal('block.deleted'), data: z.object({ id: z.string() }) }),
    z.object({ type: z.literal('settings.updated'), data: SettingsSchema }),
    z.object({ type: z.literal('helper.status'), data: z.object({ status: z.string() }) }),
    z.object({ type: z.literal('gcal.synced'), data: z.object({ count: z.number() }) }),
    z.object({ type: z.literal('heartbeat'), data: z.object({ ts: z.number() }) }),
    z.object({ type: z.literal('activity.event'), data: z.object({ ts: z.number(), count: z.number() }) }),
]);
